import CircleIcon from "@mui/icons-material/Circle";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Popper from "@mui/material/Popper";
import CircularProgress from "@mui/material/CircularProgress";

import React, { useState, useEffect, useRef } from "react";

import { FileUploadPreview } from "./FileUploadPreview";
import { codeStyle } from "./TextPreview";

import * as u from "./utility";

import { DEFAULT_FONT_SIZE, HIGHTLIGHT_COLOR } from "./constants";
import { PopupButton, PasswordInput } from "./Password";
import { decrypt } from "./crypto";
import { getRawMessageText } from "./storydat";
import { parseMarkdown, Markdown } from "./Markdown";
import {
  AttachedFile,
  MdPiece,
  MessageItem,
  SlackAttachment,
} from "./MessageItem";
import { Cipher } from "./Cipher";
import ErrorIcon from "@mui/icons-material/Error";

import { useAtomValue } from "jotai";
import { hightlightToggleAtom } from "./JotaiAtoms";
import { useSetMessageProps } from "./hooks";

function getMessageText(item: MessageItem) {
  const raw = getRawMessageText(item);
  if (raw.length > 0) {
    return raw;
  }

  if (typeof item.st_pp === "string") {
    return item.st_pp;
  }

  if (item.st_pp) {
    return JSON.stringify(item.st_pp);
  }

  return "";
}

function getAttachmentText(item: MessageItem): string | undefined {
  if (item.attachments && item.attachments.length) {
    if ("fallback" in item.attachments[0]) {
      return item.attachments[0].fallback;
    }
    if ("text" in item.attachments[0]) {
      return item.attachments[0].text;
    }
  }
  return undefined;
}

type ReactionProps = {
  item: MessageItem;
  fontSize?: number;
};

function Reaction({ item, fontSize }: ReactionProps) {
  let icons: any = { red_circle: CircleIcon, white_check_mark: CheckBoxIcon };
  let ReactionComponent = AddReactionIcon;
  const knownIcon = item.reaction && icons[item.reaction];
  if (item.reaction && knownIcon) {
    ReactionComponent = icons[item.reaction];
  }

  return (
    <Box>
      {knownIcon && <ReactionComponent />}
      {!knownIcon && (
        <Typography sx={{ fontSize: fontSize }}>
          <AddReactionIcon /> reaction: {item.reaction}
        </Typography>
      )}
    </Box>
  );
}

const closestParentWithAttr = (
  startEl: HTMLElement | null,
  attr: string,
  maxDepth: number
) => {
  let el: HTMLElement | null = startEl;
  let depth = 0;
  while (el && depth != maxDepth) {
    if (el.id === "MessagePreview") {
      return null;
    }

    if (el.hasAttribute(attr)) {
      return el;
    }
    el = el.parentElement;

    depth += 1;
  }
  return null;
};

const closestPreceedingOrParentElement = (
  startEl: HTMLElement | null,
  attr: string,
  maxDepth: number
) => {
  let el: HTMLElement | null = startEl;
  let depth = 0;
  while (el && depth != maxDepth) {
    if (el.hasAttribute(attr)) {
      return el;
    }
    const parent = el.parentElement;
    if (parent) {
      if (parent.id === "MessagePreview") {
        return null;
      }

      // we should be looking among siblings in reverse order starting with
      // the element that preceeds the current
      let children = Array.from(parent.children);
      const found = children.findIndex((a) => a === el);
      if (found != -1) {
        children = children.slice(0, found);
      }
      children.reverse();

      for (let child of children) {
        if (child !== el && child.hasAttribute(attr)) {
          return child;
        } else if (child === el) {
          break;
        }
      }
    }
    depth += 1;
    el = parent;
  }

  return null;
};

const closestFollowingOrParentElement = (
  startEl: HTMLElement | null,
  attr: string,
  maxDepth: number
) => {
  let el: HTMLElement | null = startEl;
  let depth = 0;
  while (el && depth != maxDepth) {
    if (el.hasAttribute(attr)) {
      return el;
    }
    const parent = el.parentElement;
    if (parent) {
      if (parent.id === "MessagePreview") {
        return null;
      }

      let skip = true;
      for (let child of parent.children) {
        if (skip && child !== el) {
          continue;
        } else if (child === el) {
          skip = false;
        } else if (child.hasAttribute(attr)) {
          return child;
        }
      }
    }
    depth += 1;
    el = parent;
  }
  return null;
};

const traverseMarkdownLeaves = (
  md: MdPiece[],
  func: (node: MdPiece) => void
) => {
  for (let i = 0; i < md.length; ++i) {
    const cur = md[i];
    if (cur.t == "t") {
      // tables are weird, do nothing
    } else if (typeof cur.v === "object") {
      traverseMarkdownLeaves(cur.v as MdPiece[], func);
    } else {
      func(cur);
    }
  }
};

const collectMarkedText = (md: MdPiece[]) => {
  const out: [number, number, number][] = [];
  traverseMarkdownLeaves(md, (node: MdPiece) => {
    if (node.t === "txt" && node.s?.includes("!")) {
      const begin = node.txtBlockOffset || 0;
      const end = begin + (node.v as string).length;
      if (node.txtBlockIndex === undefined) {
        throw new Error("something is off");
      }

      const idx = node.txtBlockIndex as number;
      if (
        out.length > 0 &&
        out[out.length - 1][0] === idx &&
        out[out.length - 1][2] === begin
      ) {
        out[out.length - 1][2] = end;
      } else {
        out.push([idx, begin, end]);
      }
    }
  });
  return out;
};

const rebuildMarkdown = (
  md: MdPiece[],
  func: (node: MdPiece) => MdPiece[] | null
) => {
  const out: MdPiece[] = [];
  for (let i = 0; i < md.length; ++i) {
    const cur = md[i];
    if (cur.t == "t") {
      out.push(cur);
    } else if (typeof cur.v === "object") {
      const children = rebuildMarkdown(cur.v as MdPiece[], func);
      if (children) {
        out.push({ ...cur, v: [...children] });
      }
    } else {
      const children = func(cur);
      if (children) {
        out.push(...children);
      }
    }
  }
  return out;
};

const findTextOverlap = (lhs: string, rhs: string) => {
  const maxLen = Math.min(lhs.length, rhs.length);
  for (let n = maxLen; n > 0; n--) {
    if (lhs.slice(-n) === rhs.slice(0, n)) {
      return n;
    }
  }
  return 0;
};

const addMarkStyle = (node: MdPiece) => {
  if (!node.s) {
    node.s = "!";
  } else {
    node.s += "!";
  }
};

// start and end are inclusive boundaries [start, end]
const insertMarksInMarkdown = (
  md: MdPiece[],
  start: [number, number],
  end: [number, number],
  selection: Selection
) => {
  const [startIdx, startOff] = start;
  const [endIdx, endOff] = end;

  let selectedText = selection.toString();
  if (startIdx === endIdx && startOff === endOff && selection.anchorNode) {
    return rebuildMarkdown(md, (node: MdPiece) => {
      if (
        node.t === "txt" &&
        node.txtBlockIndex !== undefined &&
        !node.s?.includes("!")
      ) {
        const txtIdx: number = node.txtBlockIndex;
        const txtOff: number = node.txtBlockOffset || 0;

        if (
          txtIdx === startIdx &&
          txtOff == startOff &&
          (node.v == selection.anchorNode?.textContent ||
            node.v === selection.focusNode?.textContent)
        ) {
          const v = node.v as string;
          let selectionOffset = 0;
          if (selection.anchorNode === selection.focusNode) {
            selectionOffset = Math.min(
              selection.anchorOffset,
              selection.focusOffset
            );
          } else if (selection.anchorNode?.textContent === v) {
            selectionOffset = selection.anchorOffset;
            selectedText =
              selection.anchorNode.textContent.slice(selectionOffset);
          } else if (selection.focusNode?.textContent === v) {
            selectionOffset = selection.focusOffset;
            selectedText =
              selection.focusNode.textContent.slice(selectionOffset);
          }

          if (v.slice(selectionOffset).startsWith(selectedText)) {
            const orig = node.v! as string;
            const prefix = { ...node };
            const postfix = { ...node };

            prefix.v = orig.slice(0, selectionOffset);

            node.v = selectedText;
            node.txtBlockOffset = selectionOffset + txtOff;
            addMarkStyle(node);

            const pos = selectionOffset + selectedText.length;
            postfix.v = orig.slice(pos);
            postfix.txtBlockOffset = pos + txtOff;
            const out = [prefix, node, postfix].filter((e) => e.v);
            return out;
          }
        }
      }
      return [node];
    });
  }

  return rebuildMarkdown(md, (node: MdPiece) => {
    if (
      node.t === "txt" &&
      node.txtBlockIndex !== undefined &&
      !node.s?.includes("!")
    ) {
      const txtIdx: number = node.txtBlockIndex;
      const txtOff: number = node.txtBlockOffset || 0;

      if (
        (txtIdx > startIdx || (txtIdx == startIdx && txtOff > startOff)) &&
        (txtIdx < endIdx || (txtIdx == endIdx && txtOff < endOff))
      ) {
        addMarkStyle(node);
      } else if (txtIdx === startIdx && txtOff == startOff) {
        // check if selection starts at the end of the first block
        const overlap = findTextOverlap(node.v as string, selectedText);
        if (overlap > 0) {
          const injectedNode = { ...node };
          const slicePos = node.v!.length - overlap;
          injectedNode.v = node.v!.slice(slicePos);
          injectedNode.txtBlockOffset = slicePos + (node.txtBlockOffset || 0);
          addMarkStyle(injectedNode);
          node.v = node.v!.slice(0, slicePos);
          return [node, injectedNode];
        } else {
          addMarkStyle(node);
        }
      } else if (txtIdx === endIdx && txtOff === endOff) {
        const overlap = findTextOverlap(selectedText, node.v as string);
        // check if selection starts in the beginning of the last block
        if (overlap > 0) {
          const injectedNode = { ...node };
          const slicePos = overlap;
          injectedNode.v = node.v!.slice(0, slicePos);
          addMarkStyle(injectedNode);

          node.v = node.v!.slice(slicePos);
          node.txtBlockOffset = slicePos + (injectedNode.txtBlockOffset || 0);
          return [injectedNode, node];
        } else {
          addMarkStyle(node);
        }
      }
    }
    return [node];
  });
};

const rebuildMarkDownWithmarks = (
  md: MdPiece[],
  marks: [number, number, number][]
) => {
  let i = 0;
  let [idx, begin, end] = marks[i];

  const moveToNextMark = () => {
    i += 1;
    if (i < marks.length) {
      [idx, begin, end] = marks[i];
    }
  };

  return rebuildMarkdown(md, (node: MdPiece) => {
    if (node.t === "txt" && i < marks.length && idx === node.txtBlockIndex) {
      const v = node.v as string;
      if (begin === 0 && end == v.length) {
        addMarkStyle(node);
        moveToNextMark();
        return [node];
      }

      const out: MdPiece[] = [];
      let off = 0;

      while (i < marks.length && idx === node.txtBlockIndex) {
        const prefix = v.slice(off, begin);
        const middle = v.slice(begin, end);
        if (prefix) {
          out.push({ ...node, txtBlockOffset: off, v: prefix });
        }
        if (middle) {
          out.push({ ...node, txtBlockOffset: off + prefix.length, v: middle });
          addMarkStyle(out[out.length - 1]);
        }
        off = end;
        moveToNextMark();
      }

      if (off < v.length) {
        out.push({ ...node, v: v.slice(off), txtBlockOffset: off });
      }

      return out;
    } else {
      return [node];
    }
  });
};

const clearMarkedText = (md: MdPiece[], idx: number, off: number) => {
  let clearFollowers = false;
  const prevMarked: MdPiece[] = [];

  const clearPreceeding = () => {
    prevMarked.forEach((e) => {
      e.s = e.s?.replaceAll("!", "");
    });
  };

  return rebuildMarkdown(md, (node: MdPiece) => {
    if (clearFollowers) {
      // clear all adjacent blocks with marks that follow the anchor
      if (node.t === "txt" && node.s?.includes("!")) {
        node.s = node.s.replaceAll("!", "");
      } else if (node.t === "txt") {
        clearFollowers = false;
      }
    } else {
      if (
        node.t === "txt" &&
        node.txtBlockIndex == idx &&
        (node.txtBlockOffset || 0) == off &&
        node.s?.includes("!")
      ) {
        node.s = node.s.replaceAll("!", "");
        clearFollowers = true;
        clearPreceeding();
      }
    }

    // collect all adjacent marked blocks
    if (node.t === "txt" && node.s?.includes("!")) {
      prevMarked.push(node);
    } else if (node.t === "txt") {
      prevMarked.length = 0;
    }

    return [node];
  });
};

type MessageTextProps = {
  item: MessageItem;
  storyKey?: string;
  fontSize?: number;
  overflow?: boolean;
  sx?: any;
};
function MessageText({
  item,
  storyKey,
  fontSize,
  overflow,
  sx,
}: MessageTextProps) {
  let [parsed, setParsed] = useState<[MdPiece[], any]>([[], {}]);
  let [includeNodeIds, setIncludeNodeIds] = useState<boolean>(false);
  const setMessageProps = useSetMessageProps();
  let highlighter = useAtomValue(hightlightToggleAtom);

  useEffect(() => {
    const parse = async (item: MessageItem) => {
      let text = getMessageText(item).trim();

      if (storyKey) {
        text = text
          .replaceAll(`STORY-${storyKey}`, "")
          .replaceAll(`[${storyKey}]`, "")
          .trim();
      }

      let parsed = await parseMarkdown(text, item);

      // do not allow scroll if embedded images are present (github images for example)
      let boxs = overflow ? { overflow: "auto", maxHeight: 400 } : {};
      for (let m of parsed) {
        if (m.t === "img") {
          boxs = {};
          break;
        }
      }

      const marks = JSON.parse(item.st_ui?.marks || null);
      if (marks && marks.length > 0) {
        const marked = rebuildMarkDownWithmarks(parsed, marks);
        parsed = marked;
      }

      setParsed([parsed, boxs]);
    };

    if (item) {
      parse(item);
    } else {
      setParsed([[], {}]);
    }
  }, [item]);

  // if (item.st_type === 'commit') {
  //   parsed =  [
  //     {
  //       "t": "txt",
  //       "v": "testing diffs: remove file and change permissions"
  //     },
  //     {
  //       "t": "txt",
  //       "v": " +0 ",
  //       "css": {
  //         "font-weight": "bold",
  //         "color": "green"
  //       }
  //     },
  //     {
  //       "t": "txt",
  //       "v": "-1 ",
  //       "css": {
  //         "font-weight": "bold",
  //         "color": "red"
  //       }
  //     },
  //     {
  //       "t": "lx",
  //       "v": "https://github.com/aautushka/test/commit/9a8e3d9e935b63c1c4ce8f0990f37a5b57be285c| 9a8e3d9 ",
  //       "css": {
  //         "font-weight": "bold",
  //         'font-size': 'x-small'
  //       }
  //     },
  //   ]
  // }

  function getSelectedNodes(selection: Selection) {
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed)
      return [];

    const range = selection.getRangeAt(0);
    const nodes = [];

    const treeWalker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          return range.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      }
    );

    while (treeWalker.nextNode()) {
      nodes.push(treeWalker.currentNode);
    }

    return nodes;
  }

  const getNodeElement = (node: Node) => {
    let el =
      node instanceof HTMLElement ? (node as HTMLElement) : node.parentElement;

    return el;
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (true || includeNodeIds) {
      const selection = window.getSelection();
      console.log(selection);
      if (
        selection &&
        !selection.isCollapsed &&
        selection.rangeCount > 0 &&
        selection.anchorNode &&
        selection.focusNode
      ) {
        const range = selection.getRangeAt(0);

        let startEl = getNodeElement(selection.anchorNode);
        let endEl = getNodeElement(selection.focusNode);
        if (
          range.startContainer?.parentElement === startEl ||
          range.endContainer?.parentElement === endEl
        ) {
          [startEl, endEl] = [endEl, startEl];
        }

        const selectedNodes = getSelectedNodes(selection);
        if (selectedNodes.length > 0) {
          startEl = getNodeElement(selectedNodes[0]);
          endEl = getNodeElement(selectedNodes[selectedNodes.length - 1]);
        }

        const startNode = closestFollowingOrParentElement(startEl, "st_md", 20);
        const endNode = closestPreceedingOrParentElement(endEl, "st_md", 20);

        let startIdx = startNode?.getAttribute("st_md");
        let endIdx = endNode?.getAttribute("st_md");
        if (endIdx && !startIdx) {
          startIdx = endIdx;
        }

        console.log(`startIdx ${startIdx}`);
        console.log(`endId ${endIdx}`);

        if (startIdx) {
          if (!endIdx) {
            endIdx = startIdx;
          }

          let start = startIdx.split(":").map((e) => Number(e)) as [
            number,
            number
          ];
          let end = endIdx.split(":").map((e) => Number(e)) as [number, number];
          if (start[0] > end[0] || (start[0] === end[0] && start[1] > end[1])) {
            [start, end] = [end, start];
          }

          const inserted = insertMarksInMarkdown(
            parsed[0],
            start,
            end,
            selection
          );

          const marks = collectMarkedText(inserted);
          setMessageProps(item, "st_ui/marks", JSON.stringify(marks));
          // console.log(marks);
          // setMessageProps(item, "st_ui/marks", JSON.stringify([]));

          const nextParsed: [MdPiece[], any] = [inserted, parsed[1]];
          setParsed(nextParsed);
        }

        window.getSelection()?.removeAllRanges();
      } else if (
        selection?.isCollapsed &&
        selection.anchorNode &&
        selection.focusNode === selection.anchorNode
      ) {
        let anchor =
          selection.anchorNode instanceof HTMLElement
            ? (selection.anchorNode as HTMLElement)
            : selection.anchorNode.parentElement;
        const txtNode = closestParentWithAttr(anchor, "st_md", 20);
        const attr = txtNode?.getAttribute("st_md");
        if (attr) {
          const [idx, off] = attr.split(":").map((e) => Number(e));
          const clear = clearMarkedText(parsed[0], idx, off);

          const marks = collectMarkedText(clear);
          setMessageProps(item, "st_ui/marks", JSON.stringify(marks));

          const nextParsed: [MdPiece[], any] = [clear, parsed[1]];
          setParsed(nextParsed);
        }
      }
      setIncludeNodeIds(false);
    }
  };

  const handleMouseDown = () => {
    if (highlighter) {
      setIncludeNodeIds(true);
      function handleOutsideClick(event: MouseEvent) {
        document.removeEventListener("mouseup", handleOutsideClick);
        handleMouseUp(event);
      }
      document.addEventListener("mouseup", handleOutsideClick);
    } else {
      setIncludeNodeIds(false);
    }
  };

  // useEffect(() => {
  //   document.body.style.userSelect = includeNodeIds ? "none" : "text";
  //   return () => {
  //     document.body.style.userSelect = "text";
  //   };
  // }, [includeNodeIds]);

  return (
    <Box
      sx={parsed[1]}
      onMouseDown={handleMouseDown}
      // onMouseUp={handleMouseUp}
      // id={includeNodeIds ? "selectable-zone" : undefined}
    >
      {includeNodeIds && (
        <style>
          {`
           ::selection {
            background: #ffd68fff;
            color: black;
           }
          `}
        </style>
      )}
      <Markdown
        parsed={parsed[0]}
        item={item}
        fontSize={fontSize}
        start={0}
        sx={sx}
        includeNodeIds={true}
      />
      {/* {parsed.length > 0 && <br />} */}
    </Box>
  );
}

type EncryptedMessageProps = {
  fontSize?: number;
  item?: MessageItem;
  storyKey?: string;
  overflow?: boolean;
  message: Cipher;
};
function EncryptedMessage({
  message,
  fontSize,
  item,
  storyKey,
  overflow,
}: EncryptedMessageProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [decrypted, setDecrypted] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [decryptInProgress, setDecryptInProgress] = useState<boolean>(false);

  const handleRequestPassword = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? undefined : e.currentTarget));
  };

  const handlePasswordCancel = () => {
    setAnchorEl(undefined);
  };

  const handlePassword = (pass: string) => {
    const decryptMessage = async () => {
      try {
        const decrypted = await decrypt(pass, message);
        setDecrypted(decrypted);
        setAnchorEl(undefined);
      } catch (e) {
        setError(true);
      }
      setDecryptInProgress(false);
    };

    setDecryptInProgress(true);
    setTimeout(() => {
      decryptMessage();
    }, 300);
  };

  return (
    <>
      {decrypted && (
        <MessageText
          item={{ ...(item ? item : { PK: "", SK: "" }), text: decrypted }}
          storyKey={storyKey}
          overflow={overflow}
          fontSize={fontSize}
        />
      )}

      {!decrypted && (
        <Stack
          direction={"row"}
          gap={2}
          sx={{
            ...codeStyle(fontSize || DEFAULT_FONT_SIZE),
            position: undefined,
            display: undefined,
            width: "fit-content",
            background: "#7b7b9e",
            pl: 3,
            pt: 1,
            pb: 1,
          }}
          alignItems={"center"}
        >
          <Box>
            <Typography fontSize={fontSize}>
              CONTAINS CONFIDENTIAL MESSAGE
            </Typography>
          </Box>
          <PopupButton
            id={anchorEl ? "encrypted-message-popper" : undefined}
            onClick={handleRequestPassword}
          >
            Reveal
          </PopupButton>
          <Popper
            id={anchorEl ? "encrypted-message-popper" : undefined}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
              }}
            >
              {decryptInProgress && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "61%",
                    top: "68%",
                    zIndex: 5,
                  }}
                >
                  <CircularProgress size={35} thickness={10} />
                </Box>
              )}

              <PasswordInput
                confirm={false}
                onPassword={handlePassword}
                onCancel={handlePasswordCancel}
                label="Enter password to reveal message"
                error={error}
              />
            </Box>
          </Popper>
        </Stack>
      )}
    </>
  );
}

type ActualPreviewProps = {
  item: MessageItem;
  storyKey?: string;
  fontSize?: number;
  onLoad?: () => void;
  overflow?: boolean;
  readOnly?: boolean;
  sx?: any;
};

function ActualPreview({
  item,
  storyKey,
  fontSize,
  overflow,
  readOnly,
  sx,
}: ActualPreviewProps) {
  const [reaction, setReaction] = useState(false);
  const [text, setText] = useState("");

  const [attachmentText, setAttachmentText] = useState<string | undefined>();

  useEffect(() => {
    const reaction = item.type === "reaction_added";
    setReaction(reaction);

    if (!reaction) {
      setText(getMessageText(item));
    }
    setAttachmentText(getAttachmentText(item));
  }, [item]);

  const getFiles = () => {
    let out: (AttachedFile | File)[] = [];
    if (item.uploaded_files) {
      out = item.uploaded_files.map((e) => e.preview || e.original);
    } else if (item.old_dummy_message?.uploaded_files) {
      out = item.old_dummy_message?.uploaded_files.map(
        (e) => e.preview || e.original
      );
    } else if (item.st_files) {
      out = item.st_files;
    } else if (item.files) {
      const imageTypes = ["jpg", "jpeg", "png", "gif", "webp"];
      item.files.forEach((f: SlackAttachment) => {
        if (f.filetype && imageTypes.includes(f.filetype)) {
          out.push({
            uri: f.url_private_download,
            size: f.size,
            type: `image/${f.filetype}`,
            name: f.name,
          });
        } else if (
          f.filetype === "binary" &&
          u.getExt({ url: f.url_private_download }) == "avif"
        ) {
          out.push({
            uri: f.url_private_download,
            size: f.size,
            type: `image/avif`,
            name: f.name,
          });
        } else if (f.mimetype) {
          out.push({
            uri: f.url_private_download,
            size: f.size,
            type: f.mimetype,
            name: f.name,
          });
        } else if (f.filetype) {
          out.push({
            uri: f.url_private_download,
            size: f.size,
            type: `application/${f.filetype}`,
            name: f.name,
          });
        }
      });
    }

    return out;
  };

  return (
    <>
      {reaction && (
        <Reaction item={item} fontSize={fontSize || DEFAULT_FONT_SIZE} />
      )}
      {text && (
        <MessageText
          item={item}
          storyKey={storyKey}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
          overflow={overflow}
          sx={sx}
        />
      )}

      {attachmentText && (
        <Typography sx={{ fontSize: fontSize || DEFAULT_FONT_SIZE }}>
          {attachmentText}
        </Typography>
      )}

      {item.st_enc && (
        <EncryptedMessage
          message={item.st_enc}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
        />
      )}

      {(item.files || item.st_files || item.uploaded_files) && (
        <FileUploadPreview
          item={item}
          files={getFiles()}
          readOnly={readOnly}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
        />
      )}
    </>
  );
}

type MessagePreviewProps = {
  item: MessageItem;
  storyKey?: string;
  fontSize?: number;
  overflow?: boolean;
  readOnly?: boolean;
  sx?: any;
};

export function MessagePreview({
  item,
  storyKey,
  fontSize,
  overflow,
  readOnly,
  sx,
}: MessagePreviewProps) {
  const [error, setError] = useState(false);
  useEffect(() => {
    if (item.error && !item.files) {
      setTimeout(() => {
        if (item.error) {
          setError(true);
        }
      }, 500);
    } else {
      setError(false);
    }
  });

  return (
    <Box id="MessagePreview">
      {item.error && !item.files && !item.st_files && (
        <Box
          sx={{
            lineHeight: 1,
            position: "relative",
            display: "inline-block",
            height: "100%",
            width: "100%",
          }}
        >
          {error && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                height: "100%",
                width: "100%",
                zIndex: 3,
              }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <ErrorIcon sx={{ color: "red" }} />
            </Box>
          )}
          <ActualPreview
            item={item}
            storyKey={storyKey}
            fontSize={fontSize}
            overflow={overflow}
            readOnly={readOnly}
            sx={sx}
          />
        </Box>
      )}
      {(!item.error || item.files || item.st_files) && (
        <ActualPreview
          item={item}
          storyKey={storyKey}
          fontSize={fontSize}
          overflow={overflow}
          readOnly={readOnly}
          sx={sx}
        />
      )}
    </Box>
  );
}

export default MessagePreview;
